import React, { useEffect, useMemo } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Spinner } from "reactstrap";
import { useForm, FieldErrors } from "react-hook-form";
import CommonForm, { FormFieldConfig } from "Components/Common/CommonForm";
import { CHECKLIST_CATEGORY_OPTIONS } from "api/modules/common.option";

interface AssignCRFCategoryModalProps {
  isOpen: boolean;
  toggle: () => void;
  selectedModule: any | null;
  existingCategory?: any;
  onSave: (payload: any) => void;
  isLoading: boolean;
}

interface FormValues {
  modulename: string | number;
  crfcategory: string | number;
}

const AssignCRFCategoryModal: React.FC<AssignCRFCategoryModalProps> = ({
  isOpen,
  toggle,
  selectedModule,
  existingCategory,
  onSave,
  isLoading,
}) => {
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    defaultValues: {
      modulename: "",
      crfcategory: "",
    },
    mode: "onChange",
  });

  useEffect(() => {
    if (selectedModule) {
      reset({
        modulename: selectedModule.label ?? selectedModule.value ?? "",
        crfcategory: existingCategory
          ? Number(existingCategory) || existingCategory
          : "",
      });
    } else {
      reset({ modulename: "", crfcategory: "" });
    }
  }, [selectedModule, existingCategory, isOpen, reset]);

  const onSubmit = (values: FormValues) => {
    const payload = {
      moduleid:
        typeof selectedModule?.value !== "undefined"
          ? selectedModule.value
          : selectedModule?.moduleid,
      crfcategory:
        typeof values.crfcategory === "object"
          ? (values.crfcategory as any).value
          : values.crfcategory,
    };
    onSave(payload);
  };

  const fields: FormFieldConfig[] = useMemo(
    () => [
      {
        name: "modulename",
        label: "Module Name",
        placeholder: "Module Name",
        type: "text",
        disabled: true,
        required: true,
      },
      {
        name: "crfcategory",
        label: "Category",
        placeholder: "Select Category",
        type: "select",
        options: CHECKLIST_CATEGORY_OPTIONS,
        required: true,
      },
    ],
    []
  );

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      centered
      backdrop="static"
      className="common_modal"
    >
      <ModalHeader toggle={toggle} className="p-3">
        {existingCategory ? "Update Category" : "Assign Category"}
      </ModalHeader>

      <ModalBody>
        <form id="assignCRFCategoryForm" onSubmit={handleSubmit(onSubmit)}>
          <CommonForm
            fields={fields}
            control={control}
            errors={errors as FieldErrors}
            columns={1}
          />
        </form>
      </ModalBody>

      <ModalFooter>
        <Button outline color="dark" size="sm" onClick={toggle}>
          Cancel
        </Button>
        <Button
          color="primary"
          size="sm"
          type="submit"
          form="assignCRFCategoryForm"
          disabled={isSubmitting || isLoading}
          outline
        >
          {isLoading && <Spinner size="sm" className="me-2" />}
          {existingCategory ? "Update" : "Save"}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default AssignCRFCategoryModal;
